import React, { useState } from "react";
import {
    Zap,
    Smartphone,
    Plane,
    CreditCard,
    Droplets,
    Flame,
    Wifi,
    Tv,
    Car,
    Bus,
    Train,
    Building,
    GraduationCap,
    Landmark,
    ShieldCheck,
    CheckCircle2,
    ArrowRight,
    HeartPulse,
    Radio,
    FileText,
    Sparkles,
    ShoppingBag,
    ShoppingCart,
    Store,
    Truck,
    Compass,
} from "lucide-react";

export const ServicesSection = ({ onSignIn, onContactClick }) => {
    // Service Switcher Interactive state
    const [selectedSolution, setSelectedSolution] = useState("electricity");
    const [consumerNumber, setConsumerNumber] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [fetchedBill, setFetchedBill] = useState(null);

    // 15 Payment Solutions matching PaySwitch
    const paymentSolutions = [
        {
            id: "electricity",
            name: "Electricity Bills",
            icon: Zap,
            biller: "TSSPDCL / BESCOM / UPPCL",
            description:
                "Instant settlement for 70+ state electricity distribution boards across India.",
            color: "text-blue-600 bg-blue-50 border-blue-200",
            fieldLabel: "Consumer Service Number",
            placeholder: "e.g. 1029384756",
            sampleAmount: 980,
        },
        {
            id: "water",
            name: "Water Bills",
            icon: Droplets,
            biller: "Municipal Corporations & Jal Boards",
            description:
                "Settle urban municipal water supply and sewerage board dues seamlessly.",
            color: "text-emerald-600 bg-emerald-50 border-emerald-200",
            fieldLabel: "Connection CAN / Consumer Number",
            placeholder: "e.g. HMWS-892301",
            sampleAmount: 420,
        },
        {
            id: "gas",
            name: "Gas & LPG Cylinders",
            icon: Flame,
            biller: "Indane / Bharat Gas / HP / IGL",
            description:
                "Book piped natural gas (PNG) and refill LPG cooking gas cylinders in seconds.",
            color: "text-amber-600 bg-amber-50 border-amber-200",
            fieldLabel: "LPG ID / Consumer No",
            placeholder: "e.g. 1726354890",
            sampleAmount: 890,
        },
        {
            id: "broadband",
            name: "Broadband & Landline",
            icon: Wifi,
            biller: "Airtel / JioFiber / BSNL / ACT",
            description:
                "Zero-interruption fiber broadband bill payments and landline settlements.",
            color: "text-blue-600 bg-blue-50 border-blue-200",
            fieldLabel: "Account ID / Telephone No",
            placeholder: "e.g. 04023456789",
            sampleAmount: 799,
        },
        {
            id: "mobile",
            name: "Mobile Prepaid & Postpaid",
            icon: Smartphone,
            biller: "Jio / Airtel / Vi / BSNL",
            description:
                "Nationwide prepaid top-ups, unlimited 5G data bundles, and postpaid bill clears.",
            color: "text-emerald-600 bg-emerald-50 border-emerald-200",
            fieldLabel: "10-Digit Mobile Number",
            placeholder: "e.g. 9848012345",
            sampleAmount: 349,
        },
        {
            id: "dth",
            name: "DTH Television",
            icon: Tv,
            biller: "Tata Play / Airtel DTH / Sun / Dish",
            description:
                "Direct-to-home satellite television subscription recharge with instant viewing.",
            color: "text-indigo-600 bg-indigo-50 border-indigo-200",
            fieldLabel: "Subscriber / SmartCard ID",
            placeholder: "e.g. 1092837465",
            sampleAmount: 450,
        },
        {
            id: "fastag",
            name: "FASTag Toll Recharge",
            icon: Car,
            biller: "NETC / All Banks FASTag",
            description:
                "National highway electronic toll payments with instant balance credit.",
            color: "text-emerald-600 bg-emerald-50 border-emerald-200",
            fieldLabel: "Vehicle Registration Number",
            placeholder: "e.g. TS09FA1234",
            sampleAmount: 500,
        },
        {
            id: "creditcard",
            name: "Credit Card Payments",
            icon: CreditCard,
            biller: "Visa / Mastercard / RuPay Cards",
            description:
                "Settle monthly outstanding balances on all major bank credit cards.",
            color: "text-blue-600 bg-blue-50 border-blue-200",
            fieldLabel: "Card Number (Last 4 Digits & Mobile)",
            placeholder: "e.g. 4321-9848012345",
            sampleAmount: 2450,
        },
        {
            id: "education",
            name: "Education & Fees",
            icon: GraduationCap,
            biller: "Schools / Colleges / Universities",
            description:
                "Direct institutional fee remittance with verified official digital fee receipts.",
            color: "text-emerald-600 bg-emerald-50 border-emerald-200",
            fieldLabel: "Student Enrollment / Roll No",
            placeholder: "e.g. SCH-2026-891",
            sampleAmount: 3500,
        },
        {
            id: "loan",
            name: "Loan EMIs & Repayments",
            icon: Landmark,
            biller: "Banks & NBFCs Across India",
            description:
                "Pay vehicle, personal, and home loan EMIs on time without penalty charges.",
            color: "text-blue-700 bg-blue-50 border-blue-200",
            fieldLabel: "Loan Account Number",
            placeholder: "e.g. LN-901827364",
            sampleAmount: 4800,
        },
        {
            id: "municipal",
            name: "Municipal Taxes & Services",
            icon: Building,
            biller: "GHMC / Civic Authorities",
            description:
                "Property tax, trade license, and municipal civic utility fee collections.",
            color: "text-cyan-600 bg-cyan-50 border-cyan-200",
            fieldLabel: "Property Tax Index (PTIN)",
            placeholder: "e.g. PTIN-10928374",
            sampleAmount: 1850,
        },
        {
            id: "housing",
            name: "Housing Society & Rent",
            icon: FileText,
            biller: "Gated Communities & Societies",
            description:
                "Apartment monthly maintenance and residential society billing payments.",
            color: "text-emerald-600 bg-emerald-50 border-emerald-200",
            fieldLabel: "Society Unit / Flat Number",
            placeholder: "e.g. BLK-B-405",
            sampleAmount: 2200,
        },
        {
            id: "hospital",
            name: "Hospital & Pathology",
            icon: HeartPulse,
            biller: "Hospitals & Diagnostic Centers",
            description:
                "Medical center diagnostics, clinical bills, and outpatient payment clearance.",
            color: "text-rose-600 bg-rose-50 border-rose-200",
            fieldLabel: "Patient UHID / Bill Reference",
            placeholder: "e.g. UHID-9928172",
            sampleAmount: 1200,
        },
        {
            id: "cable",
            name: "Cable TV & OTT",
            icon: Radio,
            biller: "Hathway / DEN / InCable / OTTs",
            description:
                "Local cable network operators and entertainment package settlements.",
            color: "text-blue-600 bg-blue-50 border-blue-200",
            fieldLabel: "Subscriber Setup Box No",
            placeholder: "e.g. STB-8839201",
            sampleAmount: 350,
        },
        {
            id: "insurance",
            name: "Insurance Premiums",
            icon: ShieldCheck,
            biller: "LIC / Max / HDFC / ICICI Pru",
            description:
                "Life, health, and motor vehicle insurance policy renewal settlements.",
            color: "text-emerald-600 bg-emerald-50 border-emerald-200",
            fieldLabel: "Policy Number",
            placeholder: "e.g. POL-918273645",
            sampleAmount: 2800,
        },
    ];

    const currentSolution =
        paymentSolutions.find((s) => s.id === selectedSolution) || paymentSolutions[0];

    const handleFetchBill = (e) => {
        e.preventDefault();
        if (!consumerNumber.trim()) return;

        setIsProcessing(true);
        setTimeout(() => {
            setIsProcessing(false);
            setFetchedBill({
                customerName: "Sriram Lakshmi R.",
                billNumber: `BILL-${Math.floor(100000 + Math.random() * 900000)}`,
                amount: currentSolution.sampleAmount,
                dueDate: "25 Sep 2026",
                biller: currentSolution.biller,
            });
        }, 600);
    };

    return (
        <div className="bg-white text-slate-900">
            {/* 1. Services Section matching PaySwitch (POS, Education, Booking) */}
            <section
                id="services-section"
                className="py-16 sm:py-24 border-b border-slate-200"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold uppercase tracking-wider mb-3">
                            Comprehensive Offerings
                        </div>
                        <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight font-['Outfit',sans-serif]">
                            Services Offered by Global Bizness
                        </h2>
                        <p className="mt-4 text-base text-slate-600">
                            Beyond utility payments, we provide cutting-edge point of sale
                            hardware, retail empowerment programs, and multi-modal travel
                            bookings.
                        </p>
                    </div>

                    {/* 5 Main Services: POS Solutions, Education & Training, Travel Booking, Grocery & Essentials, E-Commerce Solutions */}
                    <div className="space-y-8">
                        {/* Top Row: POS, Education, Travel */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Service 1: POS Solutions (Blue Accents) */}
                            <div className="bg-white rounded-3xl p-8 border border-slate-200 hover:border-blue-400 transition-all shadow-xs hover:shadow-md flex flex-col justify-between space-y-6">
                                <div className="space-y-4">
                                    <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-200 text-blue-600 flex items-center justify-center">
                                        <CreditCard className="w-7 h-7" />
                                    </div>
                                    <div>
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">
                                            Retail Hardware
                                        </span>
                                        <h3 className="text-2xl font-bold text-slate-900 font-['Outfit',sans-serif] mt-1">
                                            POS Solutions
                                        </h3>
                                    </div>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        Smart Android POS terminals, Soundbox with instant
                                        regional voice payment alerts, and dynamic all-in-one QR
                                        standees. Empowers merchants to accept debit cards, credit
                                        cards, UPI, and contactless payments effortlessly.
                                    </p>

                                    <ul className="space-y-2 pt-2 text-xs text-slate-700">
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                                            <span>Instant Soundbox voice confirmations</span>
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                                            <span>Next-day automated bank settlements</span>
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                                            <span>Low transaction processing MDR</span>
                                        </li>
                                    </ul>
                                </div>

                                <button
                                    onClick={onContactClick}
                                    className="w-full py-3 px-4 rounded-xl font-bold text-xs text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md shadow-blue-600/20 flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    <span>Inquire for POS Machine</span>
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Service 2: Education & Agent Empowerment (Green Accents) */}
                            <div className="bg-emerald-50/40 rounded-3xl p-8 border border-emerald-200 hover:border-emerald-400 transition-all shadow-xs hover:shadow-md flex flex-col justify-between space-y-6">
                                <div className="space-y-4">
                                    <div className="w-14 h-14 rounded-2xl bg-emerald-100 border border-emerald-200 text-emerald-700 flex items-center justify-center">
                                        <GraduationCap className="w-7 h-7" />
                                    </div>
                                    <div>
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                                            Retailer Empowerment
                                        </span>
                                        <h3 className="text-2xl font-bold text-slate-900 font-['Outfit',sans-serif] mt-1">
                                            Education &amp; Agent Programs
                                        </h3>
                                    </div>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        Dedicated digital training and onboarding programs that
                                        empower local grocery store owners, retailers, and
                                        distributors to operate as trusted neighborhood digital
                                        financial service centers.
                                    </p>

                                    <ul className="space-y-2 pt-2 text-xs text-slate-700">
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                            <span>Step-by-step portal operational training</span>
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                            <span>Attractive commission per bill settlement</span>
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                            <span>Dedicated regional relationship manager</span>
                                        </li>
                                    </ul>
                                </div>

                                <button
                                    onClick={onContactClick}
                                    className="w-full py-3 px-4 rounded-xl font-bold text-xs text-white bg-emerald-600 hover:bg-emerald-500 transition-colors shadow-md shadow-emerald-600/20 flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    <span>Join as Retail Partner</span>
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Service 3: Booking Services */}
                            <div className="bg-white rounded-3xl p-8 border border-slate-200 hover:border-blue-400 transition-all shadow-xs hover:shadow-md flex flex-col justify-between space-y-6">
                                <div className="space-y-4">
                                    <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-200 text-blue-600 flex items-center justify-center">
                                        <Compass className="w-7 h-7" />
                                    </div>
                                    <div>
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">
                                            Travel &amp; Mobility
                                        </span>
                                        <h3 className="text-2xl font-bold text-slate-900 font-['Outfit',sans-serif] mt-1">
                                            Booking Services
                                        </h3>
                                    </div>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        Seamless multi-modal travel bookings directly integrated
                                        with major transport operators across India, providing
                                        instant confirmed tickets and vouchers.
                                    </p>

                                    <div className="grid grid-cols-2 gap-2 pt-1 text-xs text-slate-700">
                                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2">
                                            <Bus className="w-4 h-4 text-emerald-600" />
                                            <span>Bus Tickets</span>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2">
                                            <Train className="w-4 h-4 text-blue-600" />
                                            <span>Train Tickets</span>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2">
                                            <Plane className="w-4 h-4 text-blue-600" />
                                            <span>Flight Booking</span>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2">
                                            <Building className="w-4 h-4 text-emerald-600" />
                                            <span>Hotel &amp; Stays</span>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={onContactClick}
                                    className="w-full py-3 px-4 rounded-xl font-bold text-xs text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md shadow-blue-600/20 flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    <span>Travel Booking Desk</span>
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Bottom Row: Grocery & E-Commerce */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Service 4: Grocery Distribution & Local Marts (Green Accents) */}
                            <div className="bg-emerald-50/40 rounded-3xl p-8 border border-emerald-200 hover:border-emerald-400 transition-all shadow-xs hover:shadow-md flex flex-col justify-between space-y-6">
                                <div className="space-y-4">
                                    <div className="w-14 h-14 rounded-2xl bg-emerald-100 border border-emerald-200 text-emerald-700 flex items-center justify-center">
                                        <ShoppingBag className="w-7 h-7" />
                                    </div>
                                    <div>
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                                            Supply Chain &amp; Marts
                                        </span>
                                        <h3 className="text-2xl font-bold text-slate-900 font-['Outfit',sans-serif] mt-1">
                                            Grocery &amp; Essentials Distribution
                                        </h3>
                                    </div>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        Direct wholesale procurement and reliable doorstep supply
                                        network for neighborhood Kirana stores, supermarkets, and
                                        local marts. Sourcing fresh daily staples, packaged food,
                                        beverages, and fast-moving consumer goods (FMCG) at
                                        high-margin bulk pricing.
                                    </p>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 text-xs text-slate-700">
                                        <div className="p-2.5 rounded-xl bg-white border border-emerald-200 flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                            <span>Wholesale Pricing &amp; Bulk Margins</span>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-white border border-emerald-200 flex items-center gap-2">
                                            <Truck className="w-4 h-4 text-emerald-600 shrink-0" />
                                            <span>Next-Day Doorstep Store Delivery</span>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-white border border-emerald-200 flex items-center gap-2">
                                            <Store className="w-4 h-4 text-emerald-600 shrink-0" />
                                            <span>1,000+ FMCG Brands &amp; Staples</span>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-white border border-emerald-200 flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                            <span>Digital Stock &amp; Invoicing</span>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={onContactClick}
                                    className="w-full py-3 px-4 rounded-xl font-bold text-xs text-white bg-emerald-600 hover:bg-emerald-500 transition-colors shadow-md shadow-emerald-600/20 flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    <span>Inquire for Grocery Supply</span>
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Service 5: E-Commerce Solutions (Blue Accents) */}
                            <div className="bg-white rounded-3xl p-8 border border-slate-200 hover:border-blue-400 transition-all shadow-xs hover:shadow-md flex flex-col justify-between space-y-6">
                                <div className="space-y-4">
                                    <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-200 text-blue-600 flex items-center justify-center">
                                        <ShoppingCart className="w-7 h-7" />
                                    </div>
                                    <div>
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">
                                            Digital Commerce
                                        </span>
                                        <h3 className="text-2xl font-bold text-slate-900 font-['Outfit',sans-serif] mt-1">
                                            E-Commerce Solutions
                                        </h3>
                                    </div>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        End-to-end digital retail platform enabling local
                                        shopkeepers, merchants, and distributors to launch their
                                        own branded online storefronts, showcase product catalogs,
                                        accept multi-mode digital payments, and manage doorstep
                                        customer deliveries.
                                    </p>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 text-xs text-slate-700">
                                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                                            <span>Branded Online Storefront</span>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2">
                                            <CreditCard className="w-4 h-4 text-blue-600 shrink-0" />
                                            <span>Multi-Mode Payment Gateway</span>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                                            <span>Real-Time Order &amp; Stock Tracking</span>
                                        </div>
                                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2">
                                            <Truck className="w-4 h-4 text-blue-600 shrink-0" />
                                            <span>Hyperlocal Delivery Dispatch</span>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={onContactClick}
                                    className="w-full py-3 px-4 rounded-xl font-bold text-xs text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md shadow-blue-600/20 flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    <span>Launch E-Commerce Store</span>
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 2. Our Payment Solutions matching PaySwitch */}
            <section
                id="payment-solutions-section"
                className="py-16 sm:py-24 border-b border-slate-200 bg-slate-50/50"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold uppercase tracking-wider mb-3">
                            The Payment Switch
                        </div>
                        <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight font-['Outfit',sans-serif]">
                            Our Payment Solutions
                        </h2>
                        <p className="mt-4 text-base text-slate-600">
                            Instant settlements for all essential utilities, recharges, and
                            institutional fees powered by direct BBPS integration and
                            bank-grade security.
                        </p>
                    </div>

                    {/* Interactive Live Payment Switch Console (Clean White Layout with Blue & Green Accents) */}
                    <div className="mb-16 bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-80 h-80 bg-blue-400/10 rounded-full blur-3xl pointer-events-none" />
                        <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-400/10 rounded-full blur-3xl pointer-events-none" />

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                            {/* Left Selector: Pick payment solution */}
                            <div className="lg:col-span-5 space-y-4">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-emerald-600" />
                                    <span className="text-xs font-bold uppercase tracking-wider text-blue-700">
                                        Live Bill Fetch &amp; Payment
                                    </span>
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 font-['Outfit',sans-serif]">
                                    Select Any Utility to Settle
                                </h3>
                                <p className="text-xs text-slate-500 leading-relaxed">
                                    Choose any payment solution from our network below to fetch
                                    live bill dues or execute an instant top-up.
                                </p>

                                {/* Quick grid of top solutions */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
                                    {paymentSolutions.slice(0, 9).map((sol) => {
                                        const SolIcon = sol.icon;
                                        const isSelected = selectedSolution === sol.id;
                                        return (
                                            <button
                                                key={sol.id}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedSolution(sol.id);
                                                    setFetchedBill(null);
                                                }}
                                                className={`p-2.5 rounded-xl border text-left transition-all flex items-center gap-2 cursor-pointer text-xs font-semibold ${
                                                    isSelected
                                                        ? "bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-600/20"
                                                        : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-blue-50 hover:text-blue-700"
                                                }`}
                                            >
                                                <SolIcon className="w-4 h-4 shrink-0" />
                                                <span className="truncate">
                                                    {sol.name.split(" ")[0]}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Right Form: Enter Details & Execute */}
                            <div className="lg:col-span-7 bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                                <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
                                            <currentSolution.icon className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-bold text-slate-900 font-['Outfit',sans-serif]">
                                                {currentSolution.name}
                                            </h4>
                                            <p className="text-[11px] text-slate-500">
                                                {currentSolution.biller}
                                            </p>
                                        </div>
                                    </div>
                                    <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                                        256-Bit SSL
                                    </span>
                                </div>

                                <form onSubmit={handleFetchBill} className="space-y-3">
                                    <div>
                                        <label className="text-xs font-bold text-slate-700 block mb-1">
                                            {currentSolution.fieldLabel}
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={consumerNumber}
                                            onChange={(e) => setConsumerNumber(e.target.value)}
                                            placeholder={currentSolution.placeholder}
                                            className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                                        />
                                    </div>

                                    {!fetchedBill ? (
                                        <button
                                            type="submit"
                                            disabled={isProcessing}
                                            className="w-full py-3 px-4 rounded-xl font-bold text-xs text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md shadow-blue-600/20 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                                        >
                                            {isProcessing
                                                ? "Verifying Bill Details..."
                                                : "Fetch Bill & Settle Instantly"}
                                        </button>
                                    ) : (
                                        /* Settling the bill needs a signed-in partner wallet, so
                                           the confirm CTA routes to the real sign-in screen. */
                                        <div className="p-4 bg-emerald-50/60 rounded-xl border border-emerald-200 space-y-3">
                                            <div className="flex justify-between text-xs">
                                                <span className="text-slate-600">Customer Name:</span>
                                                <span className="text-slate-900 font-bold">
                                                    {fetchedBill.customerName}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-xs">
                                                <span className="text-slate-600">Due Date:</span>
                                                <span className="text-amber-700 font-semibold">
                                                    {fetchedBill.dueDate}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-xs pt-1 border-t border-emerald-200/80">
                                                <span className="text-slate-700 font-medium">
                                                    Bill Amount:
                                                </span>
                                                <span className="text-lg font-extrabold text-emerald-700">
                                                    ₹{fetchedBill.amount}.00
                                                </span>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={onSignIn}
                                                className="w-full py-3 px-4 rounded-xl font-bold text-xs text-white bg-emerald-600 hover:bg-emerald-500 shadow-md shadow-emerald-600/30 flex items-center justify-center gap-2 cursor-pointer"
                                            >
                                                <ShieldCheck className="w-4 h-4" />
                                                <span>
                                                    Sign In to Pay ₹{fetchedBill.amount}.00
                                                </span>
                                            </button>
                                        </div>
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Full 15 Payment Solutions Cards Grid matching PaySwitch (Crisp White Cards) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                        {paymentSolutions.map((solution) => {
                            const Icon = solution.icon;
                            return (
                                <div
                                    key={solution.id}
                                    onClick={() => {
                                        setSelectedSolution(solution.id);
                                        setFetchedBill(null);
                                        const el = document.getElementById(
                                            "payment-solutions-section"
                                        );
                                        if (el) el.scrollIntoView({ behavior: "smooth" });
                                    }}
                                    className="bg-white p-5 rounded-2xl border border-slate-200 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
                                >
                                    <div className="space-y-3">
                                        <div
                                            className={`w-10 h-10 rounded-xl flex items-center justify-center ${solution.color} border transition-transform group-hover:scale-110`}
                                        >
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-sm text-slate-900 font-['Outfit',sans-serif] group-hover:text-blue-600 transition-colors">
                                                {solution.name}
                                            </h4>
                                            <p className="text-[11px] text-slate-500 line-clamp-2 mt-1">
                                                {solution.description}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="pt-3 mt-3 border-t border-slate-100 flex items-center justify-between text-[11px] font-semibold text-emerald-700">
                                        <span>Pay Now</span>
                                        <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
};
